/**
 * Bank Balances section for Entities and Personal scopes.
 *
 * Stat card at top: headline total in display currency, native per-currency
 * breakdown underneath, and an as-of timestamp with stale flag.
 *
 * Section body: accounts grouped in a two-level hierarchy:
 *   Entity > Bank > accounts
 * Both levels start collapsed: entity rows only on load, one balance per
 * entity, chevron expands to per-bank rows. Unassigned accounts get their
 * own "Unassigned" entity node.
 *
 * Entity assignment: each account row has a dropdown for assigning the account
 * to an entity. The backend writes entity_id + entity_uuid together from
 * ENTITY_DEFS so the two can never diverge.
 *
 * All conversion is client-side via currencyConvert + usePortfolioPrefsStore.
 * Backend sends native amounts and FX rates as strings.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Landmark, RefreshCw, AlertTriangle, Clock, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CurrencyToggle } from './CurrencyToggle';
import { bankAccountsAPI, entitiesAPI, bankBalanceHistoryAPI } from '../lib/api';
import { usePortfolioPrefsStore } from '../store';
import { convertAmount } from '../lib/currencyConvert';
import { formatCurrency } from '../lib/chartFormatters';
import {
  parseBalance,
  groupAccountsByEntityThenBank,
  groupAccountsByBank,
} from '../lib/bankBalancesGrouping';
import { BankGroup } from './BankBalanceRows';
import { GROUP_DISPLAY_META, GROUP_ORDER } from '../lib/jurisdictionGroups';
import { buildBankBalanceHistoryChartData } from '../lib/entityBankBalanceHistoryChart';
import { BankBalanceHistoryChart } from './dashboard/BankBalanceHistoryChart';

// ── Helpers ──────────────────────────────────────────────────────────

/** Convert a native-currency amount to display currency using the rates map. */
function toDisplay(amount, fromCurrency, toCurrency, fxRates) {
  if (amount == null) return null;
  return convertAmount(amount, fromCurrency, toCurrency, fxRates);
}

/** Format an ISO date string as a short human-readable timestamp. */
function formatAsOf(isoStr) {
  if (!isoStr) return 'Unknown';
  try {
    var d = new Date(isoStr);
    return d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

// ── Stat Card ────────────────────────────────────────────────────────

function BalanceStatCard({ accounts, fxRates, displayCurrency, newestAsOf, stale }) {
  // Per-currency native totals (preserves sign; no Math.abs)
  var nativeTotals = useMemo(function () {
    var totals = {};
    for (var i = 0; i < accounts.length; i++) {
      var acc = accounts[i];
      var bal = parseBalance(acc.balance);
      if (bal == null) continue;
      var cur = acc.currency;
      totals[cur] = (totals[cur] ?? 0) + bal;
    }
    return totals;
  }, [accounts]);

  // Converted grand total in display currency
  var grandTotal = useMemo(function () {
    var total = 0;
    for (var cur in nativeTotals) {
      var converted = toDisplay(nativeTotals[cur], cur, displayCurrency, fxRates);
      total += converted ?? 0;
    }
    return total;
  }, [nativeTotals, displayCurrency, fxRates]);

  var currencyKeys = Object.keys(nativeTotals).sort();

  return (
    <Card data-testid="bank-balances-stat-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Landmark className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">Bank Balances</p>
              <CurrencyToggle className="ml-auto" />
            </div>
            <p className="text-2xl font-bold font-mono tracking-tight" data-testid="bank-balances-headline">
              {formatCurrency(grandTotal, displayCurrency)}
            </p>
            {currencyKeys.length > 0 && (
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {currencyKeys.map(function (cur) {
                  return (
                    <span key={cur} className="text-xs text-muted-foreground font-mono">
                      {formatCurrency(nativeTotals[cur], cur)}
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {formatAsOf(newestAsOf)}
              </span>
              {stale && (
                <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] px-1.5 py-0">
                  <AlertTriangle className="h-3 w-3 mr-0.5" />
                  Stale
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Nested Entity Group ──────────────────────────────────────────────
// Two-level collapsible: entity header > bank groups > account rows.
// Entity grouping is specific to this page (not used on the dashboard card),
// so it stays local. Uses the shared BankGroup for the inner level.

export function NestedEntityGroup({ entityId, entityName, banks, subtotal, displayCurrency, fxRates, entities, onAssignEntity, assigning, defaultBankExpanded, onAccountClick }) {
  // Entity rows only on load, one balance per entity; chevron expands to the
  // per-bank rows. Was hardcoded expanded, showing every bank/account row by
  // default on both the entities and personal views.
  var [expanded, setExpanded] = useState(false);

  var accountCount = 0;
  for (var i = 0; i < banks.length; i++) {
    accountCount += banks[i].accounts.length;
  }

  return (
    <div data-testid={'entity-group-' + entityId}>
      <button
        onClick={function () { setExpanded(function (e) { return !e; }); }}
        className="flex items-center gap-3 w-full text-left py-3 px-3 bg-muted/20 hover:bg-muted/50 transition-colors"
        data-testid="entity-group-header"
      >
        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-bold flex-1">{entityName}</span>
        <span className="text-xs text-muted-foreground mr-1">
          {accountCount} acct{accountCount !== 1 ? 's' : ''}
        </span>
        <span className="text-base font-mono font-bold whitespace-nowrap" data-testid="entity-group-subtotal">
          {formatCurrency(subtotal, displayCurrency)}
        </span>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {expanded && (
        <div className="border-t border-border/40 bg-muted/10 pl-3" data-testid="entity-group-banks">
          {banks.map(function (bank) {
            return (
              <BankGroup
                key={bank.bankName}
                bankName={bank.bankName}
                accounts={bank.accounts}
                subtotal={bank.subtotal}
                displayCurrency={displayCurrency}
                fxRates={fxRates}
                defaultExpanded={defaultBankExpanded ?? false}
                subtotalMuted
                entities={entities}
                onAssignEntity={onAssignEntity}
                assigning={assigning}
                onAccountClick={onAccountClick}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Jurisdiction Section ─────────────────────────────────────────────
// Groups entity blocks under a jurisdiction header (France, United Kingdom,
// Personal). Uses the same GROUP_DISPLAY_META/GROUP_ORDER that drives the
// entity picker in DashboardLayout, so no second hardcoded list exists.

function JurisdictionSection({ label, flag, entityGroups, totalAccounts, subtotal, displayCurrency, fxRates, entities, onAssignEntity, assigning, onAccountClick }) {
  return (
    <Card data-testid={'jurisdiction-' + label.replace(/\s+/g, '-').toLowerCase()}>
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/60 rounded-t-lg">
          <span className="text-base">{flag}</span>
          <span className="text-sm font-bold">{label}</span>
          <span className="text-xs text-muted-foreground ml-auto mr-1">
            {totalAccounts} acct{totalAccounts !== 1 ? 's' : ''}
          </span>
          <span className="text-sm font-mono font-bold whitespace-nowrap" data-testid="jurisdiction-subtotal">
            {formatCurrency(subtotal, displayCurrency)}
          </span>
        </div>
        <div className="divide-y divide-border/40">
          {entityGroups.map(function (group) {
            return (
              <NestedEntityGroup
                key={group.entityId}
                entityId={group.entityId}
                entityName={group.entityName}
                banks={group.banks}
                subtotal={group.subtotal}
                displayCurrency={displayCurrency}
                fxRates={fxRates}
                entities={entities}
                onAssignEntity={onAssignEntity}
                assigning={assigning}
                onAccountClick={onAccountClick}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Group entity groups by jurisdiction using the entity metadata.
 * Returns an array of { label, flag, entityGroups, subtotal, accountCount }
 * ordered by GROUP_ORDER, with empty groups omitted.
 * "Unassigned" entities are excluded from jurisdiction groups and returned
 * separately so they render at the bottom.
 */
function buildJurisdictionSections(nestedGroups, entities) {
  // Map entity slug (type code) -> country from the entities list
  var slugToCountry = {};
  for (var i = 0; i < entities.length; i++) {
    var ent = entities[i];
    if (ent.type) slugToCountry[ent.type] = ent.country || '';
  }

  var buckets = {};
  var unassigned = [];
  for (var j = 0; j < nestedGroups.length; j++) {
    var group = nestedGroups[j];
    if (group.entityId === 'unassigned') {
      unassigned.push(group);
      continue;
    }
    var country = slugToCountry[group.entityId] || '';
    if (!buckets[country]) buckets[country] = [];
    buckets[country].push(group);
  }

  var sections = [];
  for (var k = 0; k < GROUP_ORDER.length; k++) {
    var c = GROUP_ORDER[k];
    if (buckets[c] && buckets[c].length > 0) {
      var meta = GROUP_DISPLAY_META[c];
      var jGroups = buckets[c];
      var subtotal = 0;
      var accountCount = 0;
      for (var m = 0; m < jGroups.length; m++) {
        subtotal += jGroups[m].subtotal;
        for (var n = 0; n < jGroups[m].banks.length; n++) {
          accountCount += jGroups[m].banks[n].accounts.length;
        }
      }
      sections.push({
        label: meta.label,
        flag: meta.flag,
        country: c,
        entityGroups: jGroups,
        subtotal: subtotal,
        accountCount: accountCount,
      });
      delete buckets[c];
    }
  }

  // Remaining countries not in GROUP_ORDER -> "Other"
  var remaining = [];
  for (var key in buckets) {
    if (buckets[key]) remaining = remaining.concat(buckets[key]);
  }
  if (remaining.length > 0) {
    var otherSub = 0;
    var otherCount = 0;
    for (var p = 0; p < remaining.length; p++) {
      otherSub += remaining[p].subtotal;
      for (var q = 0; q < remaining[p].banks.length; q++) {
        otherCount += remaining[p].banks[q].accounts.length;
      }
    }
    sections.push({
      label: 'Other',
      flag: '\uD83D\uDCCB',
      country: null,
      entityGroups: remaining,
      subtotal: otherSub,
      accountCount: otherCount,
    });
  }

  return { sections: sections, unassigned: unassigned };
}

// ── Main Component ───────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {'entities'|'personal'} props.scope
 * @param {string|null} props.entityId - optional filter to one entity
 */
export function BankBalancesSection({ scope, entityId, onAccountClick }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [refreshing, setRefreshing] = useState(false);
  var [entities, setEntities] = useState([]);
  var [assigning, setAssigning] = useState(false);
  var displayCurrency = usePortfolioPrefsStore(function (s) { return s.displayCurrency; });

  var fetchData = useCallback(async function () {
    try {
      var params = {};
      if (scope) params.scope = scope;
      // Handle group pseudo-entities: translate to scope/country filters
      if (entityId === 'group:personal') {
        params.scope = 'personal';
      } else if (entityId === 'group:france') {
        params.country = 'France';
      } else if (entityId === 'group:uk') {
        params.country = 'UK';
      } else if (entityId === 'group:other') {
        // Fallback group: show all (no additional filter)
      } else if (entityId && entityId !== 'all') {
        params.entity_id = entityId;
      }
      var res = await bankAccountsAPI.getSummary(params);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load bank balances');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [scope, entityId]);

  useEffect(function () {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Fetch entities for the assignment dropdown (one-time on mount)
  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      // Sort alphabetically by name for the dropdown
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {
      // Non-blocking: assignment dropdown won't render if entities fail to load
    });
  }, []);

  var handleRefresh = function () {
    setRefreshing(true);
    fetchData();
  };

  var handleAssignEntity = useCallback(async function (accountId, entityUuid) {
    setAssigning(true);
    try {
      await bankAccountsAPI.assign(accountId, { entity_uuid: entityUuid || '' });
      // Refresh balances so the account regroups under its new entity
      await fetchData();
    } catch (err) {
      setError('Assignment failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAssigning(false);
    }
  }, [fetchData]);

  // fx_rates_spot (unrounded), not fx_rates (money_to_str-quantized to 2dp --
  // a ~0.3%+ error versus the live predicate/chart's own unrounded rate).
  var fxRates = (data && data.fx_rates_spot) ? data.fx_rates_spot : {};

  // Group accounts: Entity > Bank > accounts (two-level nesting)
  var nestedGroups = useMemo(function () {
    if (!data) return [];
    return groupAccountsByEntityThenBank(data.accounts, displayCurrency, fxRates);
  }, [data, displayCurrency, fxRates]);

  // Right column: flat by-bank grouping, same primitive TransactionsPage
  // uses for its "Balances by Bank" card.
  var bankGroups = useMemo(function () {
    if (!data) return [];
    return groupAccountsByBank(data.accounts, displayCurrency, fxRates);
  }, [data, displayCurrency, fxRates]);

  // Multi-entity views: group entity blocks by jurisdiction (France, UK, Personal).
  // Uses the same GROUP_ORDER/GROUP_DISPLAY_META that drives the entity picker.
  var isMultiEntityView = !entityId || entityId === 'all' || (entityId && entityId.startsWith('group:'));
  var jurisdictionData = useMemo(function () {
    if (!isMultiEntityView || !nestedGroups.length || !entities.length) return null;
    return buildJurisdictionSections(nestedGroups, entities);
  }, [isMultiEntityView, nestedGroups, entities]);

  // Bank Balance chart (dashboards round 3, commit 7): reuses the exact
  // same calendar-year, scope-parameterized bank-balance-history series and
  // Line-chart component the entity dashboard renders (never a fork), so
  // this page and that dashboard are incapable of disagreeing on a given
  // month's total. The history endpoint only understands a single entity_id
  // OR an 'entities'/'personal' scope -- no country-level split -- so
  // group:personal (which the live fetch above also resolves to
  // scope=personal) falls back to this page's own scope prop correctly, but
  // a COUNTRY-grouped pseudo-entity (group:france/uk/other, which the live
  // fetch resolves to a country filter with no scope equivalent) has no
  // matching history granularity at all. Rendering the chart for those would
  // silently show the whole scope's total while the account list and stat
  // card right below it are France/UK/Other-only -- an on-page
  // contradiction worse than no chart, so it's skipped entirely for those
  // three pseudo-entities.
  var isCountryGroupView = entityId === 'group:france' || entityId === 'group:uk' || entityId === 'group:other';
  var [bankHistory, setBankHistory] = useState(null);
  useEffect(function () {
    if (isCountryGroupView) { setBankHistory(null); return; }
    let cancelled = false;
    var historyEntityId = isMultiEntityView ? null : entityId;
    var historyScope = isMultiEntityView ? scope : undefined;
    bankBalanceHistoryAPI.get(historyEntityId, { months: 12, displayCurrency, scope: historyScope })
      .then(function (res) { if (!cancelled) setBankHistory(res.data); })
      .catch(function () { if (!cancelled) setBankHistory(null); });
    return function () { cancelled = true; };
  }, [isCountryGroupView, isMultiEntityView, entityId, scope, displayCurrency]);

  var bankHistoryChartData = useMemo(function () {
    if (!bankHistory) return { rows: [], series: [] };
    return buildBankBalanceHistoryChartData({ monthsData: bankHistory.months, year: new Date().getFullYear() });
  }, [bankHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={handleRefresh}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.accounts.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Landmark className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No bank accounts found.</p>
          <p className="text-xs text-muted-foreground mt-1">Run a bank sync from Integrations to populate.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="bank-balances-section">
      {/* Stat card */}
      <BalanceStatCard
        accounts={data.accounts}
        fxRates={fxRates}
        displayCurrency={displayCurrency}
        newestAsOf={data.newest_as_of}
        stale={data.stale}
      />

      {/* Bank Balance chart (dashboards round 3, commit 7): calendar-year
          Line chart, same component/series as the entity dashboard. */}
      <BankBalanceHistoryChart
        rows={bankHistoryChartData.rows}
        series={bankHistoryChartData.series}
        displayCurrency={displayCurrency}
        unknownBankAccounts={bankHistory?.unknown_bank_accounts || []}
      />

      {/* Toolbar: account count, refresh */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-muted-foreground">
          {data.account_count} account{data.account_count !== 1 ? 's' : ''}
        </p>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={'h-4 w-4 mr-1' + (refreshing ? ' animate-spin' : '')} />
          Refresh
        </Button>
      </div>
      <p
        className={
          data.fx_rate_stale
            ? 'text-xs text-amber-600 dark:text-amber-500 -mt-3 text-right'
            : 'text-xs text-muted-foreground/50 -mt-3 text-right'
        }
        data-testid="fx-rate-freshness-note"
      >
        Converted to {displayCurrency} \u00B7{' '}
        {data.fx_rate_as_of
          ? `rates as of ${new Date(data.fx_rate_as_of).toLocaleString('en-GB')}`
          : 'rates unavailable'}
      </p>

      {/* Two-column layout (same grid/card primitives as the Transactions
          page's Balances by Company / Balances by Bank cards): LEFT =
          Balances by Entity (grouped under country headers), RIGHT =
          Balances by Bank. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Balances by Entity, grouped under country headers */}
        <div className="space-y-4" data-testid="balances-by-entity">
          {jurisdictionData ? (
            <>
              {jurisdictionData.sections.map(function (section) {
                return (
                  <JurisdictionSection
                    key={section.country || 'other'}
                    label={section.label}
                    flag={section.flag}
                    entityGroups={section.entityGroups}
                    totalAccounts={section.accountCount}
                    subtotal={section.subtotal}
                    displayCurrency={displayCurrency}
                    fxRates={fxRates}
                    entities={entities}
                    onAssignEntity={handleAssignEntity}
                    assigning={assigning}
                    onAccountClick={onAccountClick}
                  />
                );
              })}
              {(jurisdictionData.unassigned.length > 0 || (data.hidden_unassigned && data.hidden_unassigned.length > 0)) && (
                <Card>
                  <CardContent className="p-0">
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/60 rounded-t-lg">
                      <span className="text-base">{'\uD83D\uDCCB'}</span>
                      <span className="text-sm font-bold">Unassigned</span>
                      {data.hidden_unassigned && data.hidden_unassigned.length > 0 && (
                        <Badge variant="outline" className="text-muted-foreground text-[10px] ml-auto">
                          {data.hidden_unassigned.length} hidden
                        </Badge>
                      )}
                    </div>
                    <div className="divide-y divide-border/40">
                      {jurisdictionData.unassigned.map(function (group) {
                        return (
                          <NestedEntityGroup
                            key={group.entityId}
                            entityId={group.entityId}
                            entityName={group.entityName}
                            banks={group.banks}
                            subtotal={group.subtotal}
                            displayCurrency={displayCurrency}
                            fxRates={fxRates}
                            entities={entities}
                            onAssignEntity={handleAssignEntity}
                            assigning={assigning}
                            onAccountClick={onAccountClick}
                          />
                        );
                      })}
                      {data.hidden_unassigned && data.hidden_unassigned.length > 0 && (
                        <div className="px-3 py-2">
                          <p className="text-xs text-muted-foreground mb-2">
                            Hidden until mapped ({data.hidden_unassigned.length} account{data.hidden_unassigned.length !== 1 ? 's' : ''})
                          </p>
                          <div className="space-y-1.5">
                            {data.hidden_unassigned.map(function (acc) {
                              return (
                                <div key={acc.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className="font-mono flex-1 truncate">{acc.account_name || acc.id}</span>
                                  <span>{acc.bank_name}</span>
                                  <span className="font-mono">{acc.currency}</span>
                                  <Badge variant="outline" className="text-[9px] text-muted-foreground px-1">hidden until mapped</Badge>
                                  {entities.length > 0 && (
                                    <select
                                      className="text-[10px] border rounded px-1 py-0.5 bg-background max-w-[120px]"
                                      value=""
                                      disabled={assigning}
                                      onChange={function (e) {
                                        if (e.target.value) handleAssignEntity(acc.id, e.target.value);
                                      }}
                                    >
                                      <option value="">Assign...</option>
                                      {entities.map(function (ent) {
                                        return <option key={ent.id} value={ent.id}>{ent.name}</option>;
                                      })}
                                    </select>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-0 divide-y">
                {nestedGroups.map(function (group) {
                  return (
                    <NestedEntityGroup
                      key={group.entityId}
                      entityId={group.entityId}
                      entityName={group.entityName}
                      banks={group.banks}
                      subtotal={group.subtotal}
                      displayCurrency={displayCurrency}
                      fxRates={fxRates}
                      entities={entities}
                      onAssignEntity={handleAssignEntity}
                      assigning={assigning}
                      defaultBankExpanded={!isMultiEntityView}
                      onAccountClick={onAccountClick}
                    />
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Balances by Bank */}
        <Card data-testid="balances-by-bank">
          <CardContent className="p-0">
            <div className="flex items-center gap-2 px-4 py-3 bg-muted/60 rounded-t-lg">
              <span className="text-sm font-bold">Balances by Bank</span>
            </div>
            <div className="divide-y divide-border/40">
              {bankGroups.map(function (group) {
                return (
                  <BankGroup
                    key={group.bankName}
                    bankName={group.bankName}
                    accounts={group.accounts}
                    subtotal={group.subtotal}
                    displayCurrency={displayCurrency}
                    fxRates={fxRates}
                    defaultExpanded={false}
                    onAccountClick={onAccountClick}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
